/* iter161 — Saved Places bottom sheet for Ride Mode.
 *
 * Renders a bottom sheet with:
 *   • Pinned "+ Add New Location" row → opens the existing custom-
 *     location creation flow via router navigation to
 *     `/my-places/custom/new` (same as MySaved page's Add).
 *   • "Sort by Distance" toggle. When ON, sorts the list below by
 *     proximity to the user's current GPS location (nearest first)
 *     and replaces the row subtext with a distance readout
 *     ("2.1 km away" / "2.1 km"). When OFF, restores the original
 *     order (custom locations by created_at DESC, then favourites by
 *     created_at DESC) and the original subtext.
 *   • Combined list — user's custom locations first (pin icon,
 *     name + address), then Discover favorites (heart icon, name +
 *     category + neighborhood). Favorites only render if the user
 *     has at least one; no empty-state text for that section.
 *   • Tapping any row calls `sendRideRequest()` — the same flow
 *     used everywhere else, including History → Ride Again.
 *
 * Additive-only: this sheet is a NEW component. It does not modify
 * the existing MyPlaces / MySaved / Favorites screens.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Plus, MapPin, Heart, Loader2 } from "lucide-react";
import { api, getMe } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useGps } from "../lib/useGps";
import { haversineKm } from "../lib/geo";
import * as guestStore from "../lib/guestStorage";
import { sendRideRequest } from "../lib/sendRideRequest";
import { CategoryLabel } from "./CategoryLabel";
import { Switch } from "./ui/switch";

export default function SavedPlacesSheet({ isOpen, onClose }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { coords } = useGps();

  const [customs, setCustoms] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortByDistance, setSortByDistance] = useState(false);
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const me = await getMe();
      if (cancelled) return;
      setIsGuest(!me);
      // Custom locations
      try {
        if (me) {
          const { data } = await api.get("/custom-locations");
          if (!cancelled) setCustoms(Array.isArray(data) ? data : []);
        } else {
          if (!cancelled) setCustoms(guestStore.getCustomLocations() || []);
        }
      } catch { if (!cancelled) setCustoms([]); }

      // Favorites (Discover businesses only)
      try {
        if (me) {
          const { data: favs } = await api.get("/favorites");
          const bizFavs = (favs || []).filter((f) => f.target_type === "business");
          const rows = await Promise.all(bizFavs.map((f) =>
            api.get(`/businesses/${f.target_id}`)
              .then((r) => ({ ...r.data, _savedAt: f.created_at }))
              .catch(() => null),
          ));
          if (!cancelled) setFavorites(rows.filter(Boolean));
        } else {
          const saved = guestStore.getSaved().filter((s) => s.target_type === "business");
          const rows = await Promise.all(saved.map((s) =>
            api.get(`/businesses/${s.target_id}`)
              .then((r) => ({ ...r.data, _savedAt: s.saved_at }))
              .catch(() => null),
          ));
          if (!cancelled) setFavorites(rows.filter(Boolean));
        }
      } catch { if (!cancelled) setFavorites([]); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  // Combined list. When sorting by distance we require lat/lng to be
  // present on both types — rows without coords fall to the bottom.
  const combinedRows = useMemo(() => {
    const customRows = customs.map((c) => ({
      key: `c-${c.id}`,
      kind: "custom",
      name: c.name,
      subtext: c.address || "",
      lat: c.lat, lng: c.lng,
      raw: c,
    }));
    const favRows = favorites.map((b) => ({
      key: `b-${b.id}`,
      kind: "favorite",
      name: b.name,
      subtext: [b.category, b.neighborhood].filter(Boolean).join(" · "),
      lat: b.lat, lng: b.lng,
      raw: b,
    }));
    const rows = [...customRows, ...favRows];
    if (sortByDistance && coords) {
      return rows.slice().sort((a, b) => {
        const da = a.lat != null && a.lng != null
          ? haversineKm(coords.lat, coords.lng, a.lat, a.lng) : Infinity;
        const db = b.lat != null && b.lng != null
          ? haversineKm(coords.lat, coords.lng, b.lat, b.lng) : Infinity;
        return da - db;
      });
    }
    return rows;
  }, [customs, favorites, sortByDistance, coords]);

  const startRide = (row) => {
    sendRideRequest(
      {
        name: row.name,
        address: row.subtext,
        lat: row.lat,
        lng: row.lng,
        category: row.raw?.category,
        subcategory: row.raw?.subcategory,
      },
      { source: "ride-mode-saved-places", businessId: row.kind === "favorite" ? row.raw?.id : null },
    );
    onClose?.();
  };

  const openAddLocation = () => {
    onClose?.();
    navigate("/my-places/custom/new");
  };

  if (!isOpen) return null;
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      data-testid="saved-places-sheet"
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#171717",
          borderTop: "1px solid rgba(232,184,75,0.28)",
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          width: "100%", maxHeight: "82vh",
          display: "flex", flexDirection: "column",
          fontFamily: "'DM Sans', sans-serif",
          color: "#F5F1E8",
        }}
      >
        {/* Header */}
        <header className="flex items-center justify-between px-5 pt-5 pb-3">
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8", lineHeight: 1 }}>
            {lang === "es" ? "Lugares Guardados" : "Saved Places"}
          </h2>
          <button
            type="button"
            data-testid="saved-places-close"
            onClick={onClose}
            aria-label={lang === "es" ? "Cerrar" : "Close"}
            className="inline-flex items-center justify-center"
            style={{ width: 44, height: 44, borderRadius: 9999, background: "transparent", color: "#F5F1E8" }}
          >
            <X className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </header>

        {/* Pinned Add New Location */}
        <button
          type="button"
          data-testid="saved-places-add"
          onClick={openAddLocation}
          className="mx-5 mb-3 flex items-center gap-3 text-left"
          style={{
            minHeight: 56, padding: "0 16px",
            borderRadius: 12,
            border: "1px dashed rgba(232,184,75,0.5)",
            background: "rgba(232,184,75,0.04)",
            color: "#E8B84B",
          }}
        >
          <span
            className="inline-flex items-center justify-center"
            style={{ width: 32, height: 32, borderRadius: 9999, background: "rgba(232,184,75,0.15)" }}
          >
            <Plus className="w-4 h-4" strokeWidth={2} />
          </span>
          <span style={{ fontSize: 15, fontWeight: 600 }}>
            {lang === "es" ? "+ Añadir Nueva Ubicación" : "+ Add New Location"}
          </span>
        </button>

        {/* Sort by Distance toggle */}
        <div className="mx-5 mb-3 flex items-center justify-between" style={{ minHeight: 44 }}>
          <span style={{ fontSize: 14, color: "rgba(245,241,232,0.85)" }}>
            {lang === "es" ? "Ordenar por Distancia" : "Sort by Distance"}
          </span>
          <Switch
            checked={sortByDistance}
            onCheckedChange={setSortByDistance}
            data-testid="saved-places-sort-toggle"
            disabled={!coords}
          />
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1 px-2 pb-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: "#B0AAA3" }} />
            </div>
          ) : combinedRows.length === 0 ? (
            <div className="text-center py-10 px-4" style={{ color: "#B0AAA3", fontSize: 13 }}>
              {lang === "es"
                ? "Aún no has guardado ninguna ubicación."
                : "You haven't saved any locations yet."}
            </div>
          ) : (
            <ul className="space-y-1">
              {combinedRows.map((row) => {
                const distanceText = sortByDistance && coords && row.lat != null && row.lng != null
                  ? `${haversineKm(coords.lat, coords.lng, row.lat, row.lng).toFixed(1)} km${lang === "es" ? " · Distancia" : " away"}`
                  : row.subtext;
                const Icon = row.kind === "custom" ? MapPin : Heart;
                const iconFill = row.kind === "favorite" ? "#E8B84B" : "none";
                return (
                  <li key={row.key}>
                    <button
                      type="button"
                      data-testid={`saved-places-row-${row.key}`}
                      onClick={() => startRide(row)}
                      className="w-full flex items-center gap-3 text-left px-3"
                      style={{
                        minHeight: 60,
                        borderRadius: 10,
                        background: "transparent",
                        color: "#F5F1E8",
                      }}
                    >
                      <span
                        className="inline-flex items-center justify-center shrink-0"
                        style={{ width: 36, height: 36, borderRadius: 9999, background: "rgba(232,184,75,0.12)" }}
                      >
                        <Icon className="w-4 h-4" strokeWidth={1.6} fill={iconFill} color="#E8B84B" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block truncate" style={{ fontSize: 15, fontWeight: 500 }}>
                          {row.name}
                        </span>
                        <span
                          className="block truncate"
                          style={{ fontSize: 12, color: "rgba(126,120,109,0.9)" }}
                        >
                          {row.kind === "favorite" && !sortByDistance ? (
                            <CategoryLabel category={row.raw?.category} subcategory={row.raw?.subcategory} />
                          ) : (
                            distanceText
                          )}
                          {row.kind === "favorite" && !sortByDistance && row.raw?.neighborhood ? ` · ${row.raw.neighborhood}` : ""}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* iter176 — Footer: "Show on Map" button that opens the map
         * page filtered to only the businesses currently in this
         * user's Saved Places. */}
        <footer
          className="px-5 py-4 border-t"
          style={{ borderColor: "rgba(232,184,75,0.18)", background: "#171717" }}
        >
          <button
            type="button"
            data-testid="saved-places-show-on-map"
            onClick={() => { onClose?.(); navigate("/map?ridefilter=places"); }}
            className="w-full inline-flex items-center justify-center gap-2"
            style={{
              minHeight: 48,
              borderRadius: 12,
              background: "linear-gradient(135deg, #E8B84B, #B08C36)",
              color: "#0B0B0B",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 15,
              fontWeight: 600,
              border: "none",
              letterSpacing: "0.02em",
            }}
          >
            {lang === "es" ? "Ver en Mapa" : "Show on Map"}
          </button>
          {/* iter184 — Guest-only auth CTA. Hidden when logged in so we
              never nag returning users. Same trigger the existing
              LoginSheet / /login route uses (Emergent Google Auth). */}
          {isGuest && (
            <button
              type="button"
              data-testid="saved-places-signin"
              onClick={() => { onClose?.(); navigate("/login"); }}
              className="w-full inline-flex items-center justify-center gap-2 mt-3"
              style={{
                minHeight: 48,
                borderRadius: 12,
                background: "transparent",
                color: "#E8B84B",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 15,
                fontWeight: 600,
                border: "1px solid rgba(232,184,75,0.55)",
                letterSpacing: "0.02em",
              }}
            >
              {lang === "es" ? "Crear cuenta / Iniciar sesión" : "Create Account / Log In"}
            </button>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}
